/*
  Rebuy Gift Handler
  
  Manages code, subscription and automatic gifts with purchase:
  - Works with both manually applied and URL-based discount codes
  - Reconciles code/subscription rules and per-unit automatic gift entitlements
  - Hides quantity selectors to prevent incrementing free gifts
  - Can be toggled on/off via Admin settings
*/

window.FreeGiftHandler = (function () {
  // Private variables
  let settings = null;
  let subscriptionSettings = null;
  let validGifts = {};
  let subscriptionGiftRules = [];
  let autoGiftRules = [];
  let initialised = false;
  let reconciliationRunning = false;
  let reconciliationPending = false;
  let reconciliationPromise = null;
  let failedState = null;
  let suspendedState = null;
  const MAX_GIFT_MUTATIONS = 30;
  let debug = false;

  function log(...args) {
    if (debug) {
      console.log('[FreeGiftHandler]', ...args);
    }
  }


  // Constants
  const COOKIE_NAME = 'active_discount_code';
  const URL_COOKIE_NAME = 'url_discount_code';
  const SUBSCRIPTION_MANUAL_COOKIE_NAME = 'active_subscription_discount_code';
  const GIFT_QTY_HIDE_STYLE_ID = 'bwb-free-gift-hide-qty-controls';
  const CODE_GIFT_SOURCE = 'bwb_code';
  const SUBSCRIPTION_GIFT_SOURCE = 'bwb_subscription';
  const AUTO_GIFT_SOURCE = 'bwb_auto';

  // Cookie utilities
  function setCookie(name, value) {
    const domain = window.location.hostname;
    const secure = window.location.protocol === 'https:';
    let cookie = `${name}=${value}; path=/; domain=${domain}; max-age=86400`;
    if (secure) cookie += '; Secure';
    document.cookie = cookie;
    log(`[DE Cookie] Set: ${cookie}`);
  }

  function clearCookie(name) {
    const domain = window.location.hostname;
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${domain}`;
    // log(`[DE Cookie] Cleared: ${name}`);
  }

  function getCookie(name) {
    // log(`[Cookie Debug] Getting cookie ${name}`);
    const cookieValue = document.cookie
      .split('; ')
      .find(row => row.startsWith(`${name}=`))
      ?.split('=')[1];
    // log(`[Cookie Debug] Value for ${name}: ${cookieValue}`);
    return cookieValue;
  }

  function ensureGiftQuantityControlsHidden() {
    if (document.getElementById(GIFT_QTY_HIDE_STYLE_ID)) return;

    const styleEl = document.createElement('style');
    styleEl.id = GIFT_QTY_HIDE_STYLE_ID;
    styleEl.textContent = `
      [data-rebuy-component="cart-items"] .rebuy-cart__flyout-item[class*="property-_free_gift_source:bwb_code"] .rebuy-cart__flyout-item-quantity-widget,
      [data-rebuy-component="cart-items"] .rebuy-cart__flyout-item[class*="property-_free_gift_source:bwb_subscription"] .rebuy-cart__flyout-item-quantity-widget {
        display: none !important;
      }
      [data-rebuy-component="cart-items"] .rebuy-cart__flyout-item[class*="property-_free_gift_source:bwb_auto"] .rebuy-cart__flyout-item-quantity-widget-button,
      [data-rebuy-component="cart-items"] .rebuy-cart__flyout-item[class*="property-_free_gift_source:bwb_auto"] .rebuy-cart__flyout-item-quantity-widget button,
      [data-rebuy-component="cart-items"] .rebuy-cart__flyout-item[class*="property-_free_gift_source:bwb_auto"] .rebuy-cart__flyout-item-quantity-widget input,
      [data-rebuy-component="cart-items"] .rebuy-cart__flyout-item[class*="property-_free_gift_source:bwb_auto"] .rebuy-cart__flyout-item-quantity-widget select,
      [data-rebuy-component="cart-items"] .rebuy-cart__flyout-item[class*="property-_free_gift_source:bwb_auto"] .rebuy-cart__flyout-item-remove {
        display: none !important;
      }
    `;
    document.head.appendChild(styleEl);
  }

  // Helper to process gift configuration
  function setupGiftConfiguration() {
    if (!settings || !settings.freeGifts) return {};

    // Transform the freeGifts array into a map for easier lookup
    return settings.freeGifts.reduce((acc, gift) => {
      if (gift.discountCodes && gift.product) {
        gift.discountCodes.forEach(code => {
          const normalizedCode = code.trim().toUpperCase();
          if (normalizedCode) {
            acc[normalizedCode] = {
              product: String(gift.product),
              minSubtotal: Number(gift.minSubtotal),
            };
          }
        });
      }
      return acc;
    }, {});
  }

  function setupSubscriptionGiftConfiguration() {
    if (!subscriptionSettings || !subscriptionSettings.subscriptionGifts) return [];

    return subscriptionSettings.subscriptionGifts.reduce((acc, rule, index) => {
      const triggerVariant = String(rule?.triggerVariant || '').trim();
      const giftVariant = String(rule?.giftVariant || '').trim();
      const mode = rule?.mode === 'code_required' ? 'code_required' : 'automatic';
      const discountCodes = Array.isArray(rule?.discountCodes)
        ? rule.discountCodes
            .map(code => String(code || '').trim().toUpperCase())
            .filter(Boolean)
        : [];

      if (!triggerVariant || !giftVariant) {
        return acc;
      }

      acc.push({
        key: `${index}:${triggerVariant}:${giftVariant}`,
        triggerVariant,
        giftVariant,
        mode,
        discountCodes,
      });

      return acc;
    }, []);
  }

  function setupAutoGiftConfiguration() {
    const offers = window.autoGiftSettings?.offers;
    if (!Array.isArray(offers)) return [];

    const rules = offers.slice(0, 2).flatMap(offer => {
      if (!offer?.enabled) return [];

      const triggerProduct = String(offer.triggerProduct || '').trim();
      const triggerVariant = String(offer.triggerVariant || '').trim();
      const giftVariant = String(offer.giftVariant || '').trim();
      if (
        !/^\d+$/.test(triggerProduct) || !/^\d+$/.test(triggerVariant) || !/^\d+$/.test(giftVariant) ||
        Number(offer.triggerVariantCount) !== 1 || offer.giftPrice == null ||
        Number(offer.giftPrice) !== 0 || triggerVariant === giftVariant
      ) {
        console.warn('[FreeGiftHandler] Invalid auto gift configuration in slot', offer.slot);
        return [];
      }

      return [{ triggerProduct, triggerVariant, giftVariant }];
    });

    // A selected gift must never become a trigger for another active offer.
    return rules.filter(rule => {
      if (rules.some(other => other.giftVariant === rule.triggerVariant || other.triggerVariant === rule.giftVariant)) {
        console.warn('[FreeGiftHandler] Auto gift offers cannot award qualifying products');
        return false;
      }
      return true;
    });
  }

  function getVariantId(item) {
    return String(item.variant_id ?? item.id);
  }

  function collectAutoGiftMutations(cart) {
    const desiredQuantities = new Map();
    autoGiftRules.forEach(rule => {
      const quantity = cart.items.reduce((total, item) => {
        const isCustomGift = [CODE_GIFT_SOURCE, SUBSCRIPTION_GIFT_SOURCE, AUTO_GIFT_SOURCE]
          .includes(item.properties?._free_gift_source);
        const matchesProduct = item.product_id == null || String(item.product_id) === rule.triggerProduct;
        return !isCustomGift && matchesProduct && getVariantId(item) === rule.triggerVariant
          ? total + Number(item.quantity || 0)
          : total;
      }, 0);
      desiredQuantities.set(rule.giftVariant, (desiredQuantities.get(rule.giftVariant) || 0) + quantity);
    });

    const mutations = [];
    const retainedVariants = new Set();
    cart.items.forEach(item => {
      if (item.properties?._free_gift_source !== AUTO_GIFT_SOURCE) return;
      const variantId = getVariantId(item);
      const quantity = desiredQuantities.get(variantId) || 0;
      if (!quantity || retainedVariants.has(variantId)) {
        mutations.push({ type: 'remove', itemKey: item.key });
        return;
      }
      retainedVariants.add(variantId);
      if (item.quantity !== quantity) {
        mutations.push({ type: 'setQuantity', itemKey: item.key, quantity });
      }
    });
    desiredQuantities.forEach((quantity, variantId) => {
      if (quantity > 0 && !retainedVariants.has(variantId)) {
        mutations.push({
          type: 'add', variantId, quantity,
          properties: buildGiftAddProperties(AUTO_GIFT_SOURCE),
        });
      }
    });
    return mutations;
  }

  function normaliseCode(value) {
    return String(value || '').trim().toUpperCase();
  }

  // Detect URL-based discount codes by looking for discount messages on cart items
  function detectAutomaticDiscount(validCodes = null) {
    // Method 1: Look for line item messages (works for percentage discounts)
    const discountElements = document.querySelectorAll('.rebuy-cart__flyout-item-discount-message');
    let detectedCode = null;

    discountElements.forEach(el => {
      const discountText = el.textContent.trim().toUpperCase();
      if (!discountText) return;

      if (!validCodes || validCodes.has(discountText)) {
        detectedCode = discountText;
      }
    });

    // Method 2: Fallback to Shopify's discount_code cookie (for 0% discounts)
    if (!detectedCode) {
      const shopifyCode = normaliseCode(getCookie('discount_code'));
      if (shopifyCode && (!validCodes || validCodes.has(shopifyCode))) {
        detectedCode = shopifyCode;
        log('[GIFT DEBUG] Found code from Shopify cookie:', detectedCode);
      }
    }

    return detectedCode;
  }

  // Handle URL-based discounts
  function handleURLDiscount() {
    const detectedDiscount = detectAutomaticDiscount(new Set(Object.keys(validGifts)));

    if (detectedDiscount) {
      log(`[FreeGiftHandler] Detected automatic discount: ${detectedDiscount}`);
      setCookie(URL_COOKIE_NAME, detectedDiscount);
      return detectedDiscount;
    } else {
      clearCookie(URL_COOKIE_NAME);
      return null;
    }
  }

  function getActiveDiscountCodes() {
    const activeCodes = new Set();
    const manualDiscount = normaliseCode(getCookie(COOKIE_NAME));
    const manualSubscriptionDiscount = normaliseCode(getCookie(SUBSCRIPTION_MANUAL_COOKIE_NAME));
    const urlDiscount = normaliseCode(getCookie(URL_COOKIE_NAME));
    const automaticDiscount = normaliseCode(detectAutomaticDiscount());
    const activeDiscount = window.RebuyUtils?.getActiveDiscount?.();
    const activeDiscountCode = normaliseCode(activeDiscount?.code);
    const discountBadge = document.querySelector('.rebuy-cart__discount-tag');
    const badgeCode =
      discountBadge && discountBadge.offsetParent !== null
        ? normaliseCode(discountBadge.textContent.split(' ')[0])
        : '';

    [manualDiscount, manualSubscriptionDiscount, urlDiscount, automaticDiscount, activeDiscountCode, badgeCode].forEach(
      code => {
        if (code) {
          activeCodes.add(code);
        }
      }
    );

    return activeCodes;
  }

  function isSubscriptionLineItem(cartItem) {
    if (!cartItem || typeof cartItem !== 'object') return false;

    if (cartItem.selling_plan_allocation) return true;
    if (cartItem.selling_plan) return true;
    if (cartItem.selling_plan_id) return true;

    const properties = cartItem.properties;
    if (properties && typeof properties === 'object') {
      if (properties.selling_plan) return true;
      if (properties.selling_plan_id) return true;
      if (properties._selling_plan_id) return true;
    }

    return false;
  }

  function isManagedCodeGift(item) {
    return item?.properties?._free_gift_source === CODE_GIFT_SOURCE;
  }

  function isManagedSubscriptionGift(item) {
    return item?.properties?._free_gift_source === SUBSCRIPTION_GIFT_SOURCE;
  }

  function buildGiftAddProperties(source, triggerVariant = null) {
    const properties = {
      _free_gift_source: source,
    };

    if (triggerVariant) {
      properties._subscription_trigger_variant = String(triggerVariant);
    }

    return properties;
  }

  function collectCodeGiftMutations(cart) {
    if (!cart || !cart.items || !settings?.enableFreeGift) return [];

    const validGiftCodes = new Set(Object.keys(validGifts));
    const manualDiscount = normaliseCode(getCookie(COOKIE_NAME));
    const urlDiscount = normaliseCode(detectAutomaticDiscount(validGiftCodes));

    let activeCode = manualDiscount;
    if (!activeCode && urlDiscount) {
      activeCode = urlDiscount;
    }

    if (!activeCode && !urlDiscount) {
      clearCookie(URL_COOKIE_NAME);
    } else if (!manualDiscount && urlDiscount) {
      setCookie(URL_COOKIE_NAME, urlDiscount);
    }

    const activeGift = activeCode ? validGifts[activeCode] : null;
    const subtotal = cart.items_subtotal_price || 0;
    const allGiftVariants = Object.values(validGifts).map(g => String(g.product));
    const desiredGiftVariant =
      activeGift && subtotal >= activeGift.minSubtotal ? String(activeGift.product) : null;
    const mutations = [];

    if (desiredGiftVariant) {
      const matchingGiftItems = cart.items.filter(
        item => isManagedCodeGift(item) && String(item.id) === desiredGiftVariant
      );

      if (matchingGiftItems.length === 0) {
        mutations.push({
          type: 'add',
          variantId: desiredGiftVariant,
          properties: buildGiftAddProperties(CODE_GIFT_SOURCE),
        });
      } else {
        const [primaryGift, ...duplicateGifts] = matchingGiftItems;

        if (primaryGift.quantity !== 1) {
          mutations.push({ type: 'setQuantity', itemKey: primaryGift.key, quantity: 1 });
        }

        duplicateGifts.forEach(item => {
          mutations.push({ type: 'remove', itemKey: item.key });
        });
      }
    }

    cart.items.forEach(item => {
      const isOurFreeGift = isManagedCodeGift(item);
      const isKnownGiftVariant = allGiftVariants.includes(String(item.id));

      if (
        isOurFreeGift &&
        parseInt(item.price, 10) === 0 &&
        isKnownGiftVariant &&
        (!desiredGiftVariant || String(item.id) !== desiredGiftVariant)
      ) {
        mutations.push({ type: 'remove', itemKey: item.key });
      }
    });

    if (!manualDiscount || !validGifts[manualDiscount]) {
      clearCookie(COOKIE_NAME);
    }

    return mutations;
  }

  function doesSubscriptionRuleQualify(rule, cart, activeCodes) {
    if (!rule || !cart?.items) return false;

    const hasTriggerLine = cart.items.some(
      item => String(item.id) === rule.triggerVariant && isSubscriptionLineItem(item)
    );

    if (!hasTriggerLine) {
      return false;
    }
    if (rule.mode === 'automatic') {
      return true;
    }

    const matchedCode = rule.discountCodes.find(code => activeCodes.has(code));
    return !!matchedCode;
  }

  function collectSubscriptionGiftMutations(cart, activeCodes) {
    if (!cart || !cart.items || !subscriptionSettings?.enableSubscriptionGifts) return [];

    const mutations = [];
    const desiredGiftKeys = new Set();
    const subscriptionGiftItems = cart.items.filter(isManagedSubscriptionGift);

    subscriptionGiftRules.forEach(rule => {
      if (!doesSubscriptionRuleQualify(rule, cart, activeCodes)) {
        return;
      }

      desiredGiftKeys.add(`${rule.triggerVariant}:${rule.giftVariant}`);

      const matchingGiftItems = subscriptionGiftItems.filter(
        item =>
          String(item.id) === rule.giftVariant &&
          String(item.properties?._subscription_trigger_variant || '') === rule.triggerVariant
      );

      if (matchingGiftItems.length === 0) {
        mutations.push({
          type: 'add',
          variantId: rule.giftVariant,
          properties: buildGiftAddProperties(SUBSCRIPTION_GIFT_SOURCE, rule.triggerVariant),
        });
        return;
      }

      const [primaryGift, ...duplicateGifts] = matchingGiftItems;

      if (primaryGift.quantity !== 1) {
        mutations.push({ type: 'setQuantity', itemKey: primaryGift.key, quantity: 1 });
      }

      duplicateGifts.forEach(item => {
        mutations.push({ type: 'remove', itemKey: item.key });
      });
    });

    subscriptionGiftItems.forEach(item => {
      const triggerVariant = String(item.properties?._subscription_trigger_variant || '');
      const giftKey = `${triggerVariant}:${String(item.id)}`;

      if (!desiredGiftKeys.has(giftKey)) {
        mutations.push({ type: 'remove', itemKey: item.key });
      }
    });

    return mutations;
  }

  function collectGiftMutations(cart, activeCodes) {
    const mutations = [
      ...collectAutoGiftMutations(cart),
      ...collectCodeGiftMutations(cart),
      ...collectSubscriptionGiftMutations(cart, activeCodes),
    ];
    // Preserve the old shared one-unit cap, even if only the other legacy
    // promotion is enabled. Apply it once through the same awaited worker.
    if (settings?.enableFreeGift || subscriptionSettings?.enableSubscriptionGifts) {
      const codeVariants = new Set(Object.values(validGifts).map(gift => gift.product));
      cart.items.forEach(item => {
        const isCodeGift = isManagedCodeGift(item) && Number(item.price) === 0 && codeVariants.has(String(item.id));
        if ((isCodeGift || isManagedSubscriptionGift(item)) && item.quantity > 1 &&
            !mutations.some(mutation => mutation.itemKey === item.key)) {
          mutations.push({ type: 'setQuantity', itemKey: item.key, quantity: 1 });
        }
      });
    }
    return mutations;
  }

  function getReconciliationState(cart, activeCodes) {
    return JSON.stringify({
      items: cart.items.map(item => [item.key, getVariantId(item), item.quantity, item.price,
        item.properties, item.selling_plan_allocation || item.selling_plan || item.selling_plan_id]),
      subtotal: cart.items_subtotal_price,
      codes: [...activeCodes].sort(),
    });
  }

  async function applyMutation(mutation) {
    if (mutation.type === 'add') {
      return window.RebuyUtils.addGiftToCart(mutation.variantId, mutation.properties, mutation.quantity);
    }
    // Never fall back to a variant ID: another promotion may own the same variant.
    if (!mutation.itemKey) throw new Error('Gift mutation requires a cart line key');
    if (mutation.type === 'remove') {
      return window.RebuyUtils.removeItemFromCart(mutation.itemKey);
    }
    return window.RebuyUtils.setItemQuantity(mutation.itemKey, mutation.quantity);
  }

  async function reconcileGifts() {
    let attemptedMutation = false;
    let mutationCount = 0;
    try {
      while (true) {
        reconciliationPending = false;
        // Rebuy refresh can emit cart events itself. Read Shopify directly here.
        const cart = await window.RebuyUtils.fetchCart({ useRebuy: false });
        if (!Array.isArray(cart?.items)) break;
        const activeCodes = getActiveDiscountCodes();
        const mutations = collectGiftMutations(cart, activeCodes);
        const state = getReconciliationState(cart, activeCodes);
        if (state === suspendedState || state === failedState) break;
        if (!mutations.length) {
          if (reconciliationPending) continue;
          break;
        }
        if (mutationCount >= MAX_GIFT_MUTATIONS) {
          suspendedState = state;
          console.warn('[FreeGiftHandler] Gift reconciliation paused after repeated cart changes');
          break;
        }

        attemptedMutation = true;
        const result = await applyMutation(mutations[0]);
        mutationCount += 1;
        if (!result) {
          // An add may have reached Shopify despite a lost response. The next
          // attempt must re-read the cart; matching failed states stay suppressed.
          failedState = state;
          break;
        }
        // Recalculate from a fresh cart after every write, including new line keys.
      }
    } finally {
      if (attemptedMutation && typeof window.Rebuy?.Cart?.getCart === 'function') {
        try {
          await window.Rebuy.Cart.getCart();
        } catch (error) {
          console.error('[FreeGiftHandler] Could not refresh Rebuy after gift changes:', error);
        }
      }
    }
  }

  function manageGift(retryFailed = false) {
    reconciliationPending = true;
    if (reconciliationRunning) return reconciliationPromise;
    if (retryFailed) {
      failedState = null;
      suspendedState = null;
    }
    reconciliationRunning = true;
    reconciliationPromise = reconcileGifts()
      .catch(error => {
        reconciliationPending = false;
        console.error('[FreeGiftHandler] Gift reconciliation failed:', error);
      })
      .finally(() => {
        reconciliationRunning = false;
        if (reconciliationPending) manageGift();
      });
    return reconciliationPromise;
  }

  // Handle manual discount code application
  function setupDiscountListeners() {
    document.body.addEventListener('click', async e => {
      const applyBtn = e.target.closest('.rebuy-cart__discount-form button');
      if (!applyBtn) return;

      const input = document.querySelector('#rebuy-cart__discount-input');
      const code = input?.value?.trim()?.toUpperCase();
      if (!code) return;

      const isCodeGiftCode = !!validGifts[code];
      const isSubscriptionGiftCode = subscriptionGiftRules.some(rule => rule.discountCodes.includes(code));

      if (!isCodeGiftCode && !isSubscriptionGiftCode) return;

      if (isCodeGiftCode) {
        log(`Applying gift code: ${code}`);
        setCookie(COOKIE_NAME, code);

        // Verify cookie was set
        await new Promise(resolve => setTimeout(resolve, 300));
        if (getCookie(COOKIE_NAME) !== code) {
          console.warn('Cookie not persisted! Using localStorage fallback');
          localStorage.setItem(`gift_code_fallback`, code);
        }
      }

      if (isSubscriptionGiftCode) {
        log(`Applying subscription gift code: ${code}`);
        setCookie(SUBSCRIPTION_MANUAL_COOKIE_NAME, code);
      }
    });
  }

  function setupRemovalListeners() {
    document.body.addEventListener('click', async e => {
      if (!e.target.closest('.rebuy-cart__discount-tag-remove')) return;

      const currentCode = getCookie(COOKIE_NAME);
      // ONLY clear if it's one of our gift codes
      if (currentCode && validGifts[currentCode.toUpperCase()]) {
        log(`Clearing gift code cookie: ${currentCode}`);
        clearCookie(COOKIE_NAME);
      }

      const currentSubscriptionCode = getCookie(SUBSCRIPTION_MANUAL_COOKIE_NAME);
      // ONLY clear if it's one of our subscription gift codes
      if (
        currentSubscriptionCode &&
        subscriptionGiftRules.some(rule => rule.discountCodes.includes(currentSubscriptionCode.toUpperCase()))
      ) {
        log(`Clearing subscription gift code cookie: ${currentSubscriptionCode}`);
        clearCookie(SUBSCRIPTION_MANUAL_COOKIE_NAME);
      }
    });
  }

  // Public API
  return {
    init: function () {
      if (initialised || !window.RebuyEventManager) return this;
      initialised = true;
      settings = window.discountGiftSettings || {};
      subscriptionSettings = window.subscriptionGiftSettings || {};
      const hasLegacyGifts = settings.enableFreeGift || subscriptionSettings.enableSubscriptionGifts;
      validGifts = hasLegacyGifts ? setupGiftConfiguration() : {};
      subscriptionGiftRules = hasLegacyGifts ? setupSubscriptionGiftConfiguration() : [];
      autoGiftRules = setupAutoGiftConfiguration();
      ensureGiftQuantityControlsHidden();
      // Delegated listeners already cover dynamically rendered discount controls.
      setupRemovalListeners();
      setupDiscountListeners();

      window.RebuyEventManager.on('cart:change', () => manageGift())
        .on('cart:ready', () => manageGift())
        .on('cart:show', () => manageGift(true))
        .on('discount:applied', () => manageGift())
        .on('discount:removed', () => manageGift());
      // Also handles restored carts if Rebuy's ready event preceded registration.
      manageGift();
      return this;
    },

    debug: function (enabled) {
      debug = enabled;
      log('Debug logging', enabled ? 'enabled' : 'disabled');
      return this;
    },

    // Helper method to check if a code is valid for a gift
    isValidGiftCode: function (code) {
      if (!code) return false;
      const normalizedCode = code.trim().toUpperCase();
      return !!validGifts[normalizedCode];
    },

    // Helper to get current active gift code
    getActiveCode: function () {
      // Try cookie first
      const cookieValue = getCookie(COOKIE_NAME) || getCookie(URL_COOKIE_NAME);
      if (cookieValue) return cookieValue;

      // If cookie fails, check localStorage
      try {
        return localStorage.getItem(`gift_code_${COOKIE_NAME}`);
      } catch (e) {
        return null;
      }
    },
  };
})();
